import { ReactNode, useMemo } from 'react'
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts'
import { useObjectListAll } from '../api/object'
import { useReportingLocationDistribution } from '../api/reporting'
import { Paper } from '../components/paper'

export function LocationUtilization({
	range,
	locationType,
	title,
}: {
	range: { start: string; end: string }
	locationType: 'locationFrom' | 'locationTo'
	title: ReactNode
}) {
	const { data: locations = [] } = useObjectListAll('location')
	const locationsMap = useMemo(() => {
		return new Map(locations.map(location => [location.id, location]))
	}, [locations])

	const query = useReportingLocationDistribution({
		dateFrom: range.start,
		dateTo: range.end,
		locationType,
	})

	const data = useMemo(() => {
		if (!query.data || query.data.length === 0) return []
		return (
			query.data[0]?.locationStats.map(stat => {
				const locationId = stat.locationId
				const location = locationsMap.get(locationId)
				return {
					...stat,
					name: location?.name || locationId,
				}
			}) || []
		)
	}, [query.data, locationsMap])

	return (
		<Paper className="p-4">
			<h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
			<div className="h-64">
				<ResponsiveContainer width="100%" height="100%">
					<PieChart>
						<Pie
							data={data}
							dataKey="percentage"
							nameKey="name"
							cx="50%"
							cy="50%"
							outerRadius={80}
							label
						>
							{data.map(({ locationId }) => (
								<Cell
									key={locationId}
									fill={locationsMap.get(locationId)?.color ?? 'black'}
								/>
							))}
						</Pie>
						<Tooltip formatter={(value: number) => `${value.toFixed(2)}%`} />
						<Legend />
					</PieChart>
				</ResponsiveContainer>
			</div>
		</Paper>
	)
}
